// src/hooks/useSlideshow.js
import {useCallback, useState} from "react";
import toast from "react-hot-toast";

/**
 * Hook to manage slideshow state and note loading
 * @param {string} apiUrl - Base API URL
 * @param {string|number} userId - Current user ID
 * @returns {Object} { slideshowOpen, slideshowNotes, slideshowJobName, slideshowStartIndex, loadingSlideshowJob, setSlideshowOpen, handleOpenSlideshow }
 */
export const useSlideshow = (apiUrl, userId) => {
  const [slideshowOpen, setSlideshowOpen] = useState(false);
  const [slideshowNotes, setSlideshowNotes] = useState([]);
  const [slideshowJobName, setSlideshowJobName] = useState("");
  const [slideshowStartIndex, setSlideshowStartIndex] = useState(0);
  const [loadingSlideshowJob, setLoadingSlideshowJob] = useState(null);

  const handleOpenSlideshow = useCallback(
    async (job, startIndex = 0) => {
      const jobId = job.jobId || job.id || job.jobID;
      setLoadingSlideshowJob(jobId);
      try {
        if (job.notes && job.notes.length > 0) {
          setSlideshowNotes(job.notes);
          setSlideshowJobName(job.jobName);
          setSlideshowStartIndex(startIndex);
          setSlideshowOpen(true);
        } else {
          const toastId = toast.loading(`Loading notes for ${job.jobName}...`);
          const params = new URLSearchParams({
            pageNumber: "1",
            pageSize: "1000",
            jobId,
            userId,
          });
          const response = await fetch(
            `${apiUrl}/api/SiteNote/GetSiteNotesByJobId?${params.toString()}`
          );
          if (!response.ok) throw new Error(`Failed to fetch notes: ${response.status}`);
          const data = await response.json();
          const formattedNotes = (data.siteNotes || []).map((n) => ({
            ...n,
            id: n.id,
            userName: n.userName || n.UserName || "Unknown",
            documentCount: n.documentCount || 0,
            timeStamp: n.timeStamp || n.date,
            date: n.date || n.createdDate,
            workspace: n.workspace || "",
            project: n.project || "",
            job: n.job || job.jobName,
            note: n.note || n.content,
            jobId: n.jobId || jobId,
          }));
          job.notes = formattedNotes;
          setSlideshowNotes(formattedNotes);
          setSlideshowJobName(job.jobName);
          setSlideshowStartIndex(startIndex);
          setSlideshowOpen(true);
          toast.dismiss(toastId);
          toast.success(`Loaded ${formattedNotes.length} notes for slideshow`);
        }
      } catch (error) {
        console.error("Error fetching notes for slideshow:", error);
        toast.error(`Failed to load notes: ${error.message}`);
      } finally {
        setLoadingSlideshowJob(null);
      }
    },
    [apiUrl, userId]
  );

  return {
    slideshowOpen,
    slideshowNotes,
    slideshowJobName,
    slideshowStartIndex,
    loadingSlideshowJob,
    setSlideshowOpen,
    handleOpenSlideshow,
  };
};